/** Formatting for the page. Pure, so it is tested rather than eyeballed. */

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) =>
    char === "&" ? "&amp;" : char === "<" ? "&lt;" : char === ">" ? "&gt;" : char === '"' ? "&quot;" : "&#39;",
  );
}

/** `1:34:40` or `9:40` — a clock, the same in every language. */
export function clock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${String(h)}:${pad(m)}:${pad(s)}` : `${String(m)}:${pad(s)}`;
}

/** `6h 5m` — a length, with the reader's unit suffixes. */
export function length(seconds: number, units: { d: string; h: string; m: string; s: string }): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${String(s)}${units.s}`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${String(m)}${units.m}`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${String(h)}${units.h} ${String(m % 60)}${units.m}`;
  return `${String(Math.floor(h / 24))}${units.d} ${String(h % 24)}${units.h}`;
}

/**
 * A calendar date, built from its parts: `new Date("2026-08-31")` is UTC midnight, which
 * renders as the 30th everywhere west of Greenwich.
 */
export function calendarDate(value: string): Date | undefined {
  const digits = value.replace(/-/g, "");
  if (!/^\d{8}$/.test(digits)) return undefined;
  const date = new Date(Number(digits.slice(0, 4)), Number(digits.slice(4, 6)) - 1, Number(digits.slice(6, 8)), 12);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

export function formatDate(value: string, locale: string): string {
  const date = calendarDate(value);
  return date === undefined ? value : date.toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
}

/** "7 weeks ago", in the reader's language. */
export function ago(value: string, now: number, locale: string): string {
  const date = calendarDate(value);
  if (date === undefined) return "";
  const days = Math.round((now - date.getTime()) / 86_400_000);
  const format = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (days < 14) return format.format(-days, "day");
  if (days < 60) return format.format(-Math.round(days / 7), "week");
  return format.format(-Math.round(days / 30), "month");
}

/** Where the VOD on the air is now, extrapolated from the daemon's last anchor. */
export function positionNow(
  playing: { durationSeconds: number; positionSeconds?: number; positionAt?: number; paused?: boolean },
  now: number,
): number | undefined {
  if (playing.positionSeconds === undefined || playing.positionAt === undefined) return undefined;
  const moved = playing.paused === true ? 0 : (now - playing.positionAt) / 1_000;
  return Math.max(0, Math.min(playing.durationSeconds, playing.positionSeconds + moved));
}

/**
 * The winner to show in a closed vote. A tie was settled at random by the daemon, and
 * `winnerIndex` is that result. The fallback is for a daemon on an older build that sends
 * no `winnerIndex`: on a tie it shows the first tied candidate, which may not be the one
 * that was queued.
 */
export function winnerIndex(vote: {
  counts: readonly number[];
  leadingIndex: number | undefined;
  winnerIndex?: number;
}): number {
  if (vote.winnerIndex !== undefined) return vote.winnerIndex;
  if (vote.leadingIndex !== undefined) return vote.leadingIndex;
  const counts = vote.counts;
  return counts.indexOf(Math.max(...counts));
}
